const DAY_LABELS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const DAY_ALIASES = {
  montag: 0,
  mo: 0,
  monday: 0,
  mon: 0,
  dienstag: 1,
  di: 1,
  tuesday: 1,
  tue: 1,
  tues: 1,
  mittwoch: 2,
  mi: 2,
  wednesday: 2,
  wed: 2,
  donnerstag: 3,
  do: 3,
  thursday: 3,
  thu: 3,
  thur: 3,
  thurs: 3,
  freitag: 4,
  fr: 4,
  friday: 4,
  fri: 4,
  samstag: 5,
  sa: 5,
  saturday: 5,
  sat: 5,
  sonntag: 6,
  so: 6,
  sunday: 6,
  sun: 6,
};

const cleanText = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const normalizeDayToken = (value) => cleanText(value)
  .toLocaleLowerCase('de-DE')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z]/g, '');

const getDayIndex = (value) => {
  const token = normalizeDayToken(value);
  return Object.prototype.hasOwnProperty.call(DAY_ALIASES, token) ? DAY_ALIASES[token] : null;
};

const atLocalNoon = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);

const parseLocalDate = (value) => {
  const text = cleanText(value);
  if (!text || text.toLowerCase() === 'null') return null;

  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    const parsed = new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]), 12, 0, 0, 0);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : atLocalNoon(parsed);
};

const startOfCurrentWeek = (referenceDate) => {
  const date = atLocalNoon(referenceDate);
  const mondayOffset = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - mondayOffset);
  return date;
};

export const resolveCommercialMenuDate = (entry, referenceDate = new Date()) => {
  const explicitDate = parseLocalDate(entry?.date);
  if (explicitDate) return explicitDate;

  const dayIndex = getDayIndex(entry?.day);
  if (dayIndex === null) return null;

  const resolved = startOfCurrentWeek(referenceDate);
  resolved.setDate(resolved.getDate() + dayIndex);
  return resolved;
};

const isSameLocalDate = (first, second) => Boolean(first && second)
  && first.getFullYear() === second.getFullYear()
  && first.getMonth() === second.getMonth()
  && first.getDate() === second.getDate();

const parseMenuValue = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const normalizeCommercialMenu = (value, referenceDate = new Date()) => {
  const today = atLocalNoon(referenceDate);

  return parseMenuValue(value)
    .map((rawEntry, originalIndex) => {
      const entry = rawEntry && typeof rawEntry === 'object' ? rawEntry : { meal: rawEntry };
      const resolvedDate = resolveCommercialMenuDate(entry, today);
      const meal = cleanText(entry.meal || entry.title || entry.name);
      const price = cleanText(entry.price);
      const explicitDay = getDayIndex(entry.day);

      return {
        ...entry,
        originalIndex,
        meal,
        price,
        resolvedDate,
        dateLabel: resolvedDate ? resolvedDate.toLocaleDateString('de-DE') : '',
        dayLabel: resolvedDate
          ? DAY_LABELS[(resolvedDate.getDay() + 6) % 7]
          : (explicitDay !== null ? DAY_LABELS[explicitDay] : cleanText(entry.day)),
        isToday: isSameLocalDate(resolvedDate, today),
      };
    })
    .filter((entry) => entry.meal || entry.price || entry.dayLabel || entry.dateLabel)
    .sort((first, second) => {
      if (first.resolvedDate && second.resolvedDate) {
        const dateDifference = first.resolvedDate - second.resolvedDate;
        if (dateDifference !== 0) return dateDifference;
      } else if (first.resolvedDate) {
        return -1;
      } else if (second.resolvedDate) {
        return 1;
      }

      return first.originalIndex - second.originalIndex;
    });
};

export const isCommercialMenuCatalog = (offer) => (
  offer?.is_menu === true
  || offer?.isMenu === true
  || offer?.is_menu === 1
  || String(offer?.is_menu).toLowerCase() === 'true'
);

export const buildCommercialFeedItems = (offer, referenceDate = new Date()) => {
  if (isCommercialMenuCatalog(offer)) {
    return [{
      ...offer,
      feedKey: `menu-${offer.id}`,
      feedMode: 'menu',
      displayTitle: offer.title || 'Speisekarte',
      displayDescription: offer.description || '',
    }];
  }

  const entries = normalizeCommercialMenu(offer.weekly_menu, referenceDate);
  if (entries.length === 0) {
    return [{
      ...offer,
      feedKey: `offer-${offer.id}`,
      feedMode: 'offer',
      displayTitle: offer.title,
      displayDescription: offer.description || '',
    }];
  }

  return entries
    .filter((entry) => entry.isToday)
    .map((entry) => ({
      ...offer,
      feedKey: `meal-${offer.id}-${entry.originalIndex}`,
      feedMode: 'meal',
      displayTitle: entry.meal || offer.title,
      displayDescription: offer.title && offer.title !== entry.meal ? offer.title : (offer.description || ''),
      displayPrice: entry.price,
      displayDate: entry.dateLabel,
      displayDay: entry.dayLabel,
      menuEntry: entry,
    }));
};

// Ein Angebot ist am Gültigkeitstag selbst noch gültig (valid_until ist ein Datum ohne Uhrzeit).
export const isCommercialOfferActive = (offer, referenceDate = new Date()) => {
  if (!offer || offer.is_active === false) return false;
  const validUntil = parseLocalDate(offer.valid_until);
  return !validUntil || validUntil >= atLocalNoon(referenceDate);
};

// Nur tagesgebundene Gerichte, die heute gelten – unabhängig davon, ob das Angebot
// als Speisekarte markiert ist. Angebote ohne Tagesbezug liefern eine leere Liste.
export const getTodayMenuEntries = (offer, referenceDate = new Date()) => {
  if (!isCommercialOfferActive(offer, referenceDate)) return [];
  return normalizeCommercialMenu(offer.weekly_menu, referenceDate)
    .filter((entry) => entry.isToday && entry.meal);
};
